import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, ArrowRight, Linkedin, Twitter, Facebook, Instagram, Youtube, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

// Logo with white background for visibility on dark footer
const LOGO_WHITE_BG = 'https://customer-assets.emergentagent.com/job_modern-advisors/artifacts/3cgus5vs_Shire%20Logo%20-%20small.png';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Panel ID to navigation link mapping
const panelNavConfig = {
  hero: { name: 'Home', href: '#home' },
  services: { name: 'Services', href: '#services' },
  about: { name: 'About Us', href: '#about' },
  testimonials: { name: 'Testimonials', href: '#testimonials' },
  'case-studies': { name: 'Case Studies', href: '#case-studies' },
  investments: { name: 'Investments', href: '#investments' },
  faq: { name: 'FAQ', href: '#faq' },
  insights: { name: 'Insights', href: '#insights' },
  contact: { name: 'Contact', href: '#contact' },
};

// Legal Modal Component
const LegalModal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-navy-900">{title}</h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)] text-gray-600 text-sm leading-relaxed">
          {children}
        </div>
      </div>
    </div>
  );
};

const Footer = ({ settings }) => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const navigate = useNavigate();
  const [visiblePanels, setVisiblePanels] = useState([]);
  const [services, setServices] = useState([]);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [socialMedia, setSocialMedia] = useState(null); // null = not loaded yet, [] = loaded but empty
  const [ctaHeader, setCtaHeader] = useState({
    title: "Ready to Transform Your Business?",
    description: "Schedule a confidential consultation with our team today."
  });
  const [contactInfo, setContactInfo] = useState({
    phone: "(214) 673-2710",
    phone_alt: "",
    email: "info@shireadvisors.com",
    address: "516 South I-35E, Denton, TX 76205",
  });
  
  // Fetch visible panels, services, social media, and CTA header
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch panel layout
        const layoutRes = await axios.get(`${API}/settings/layout`);
        if (layoutRes.data.panels) {
          setVisiblePanels(layoutRes.data.panels);
        }
        
        // Fetch services (visible only)
        const servicesRes = await axios.get(`${API}/content/services`);
        if (servicesRes.data) {
          setServices(servicesRes.data.filter(s => s.visible !== false));
        }
        
        // Fetch social media settings
        const socialRes = await axios.get(`${API}/settings/social-media`);
        if (socialRes.data.social_media) {
          // Only show items that are enabled AND have a URL
          setSocialMedia(socialRes.data.social_media.filter(s => s.enabled && s.url));
        } else {
          setSocialMedia([]); // Explicitly set empty array
        }
        
        // Fetch CTA header
        const ctaRes = await axios.get(`${API}/content/section-headers/footer_cta`);
        if (ctaRes.data) {
          setCtaHeader(ctaRes.data);
        }

        // Fetch dynamic contact info
        const contactRes = await axios.get(`${API}/content/contact`);
        if (contactRes.data) {
          setContactInfo((prev) => ({ ...prev, ...contactRes.data }));
        }
      } catch (error) {
        console.log('Using default footer data');
        setSocialMedia([]); // On error, don't show defaults
      }
    };
    
    fetchData();
  }, []);

  // Auto version — derived from the last deploy timestamp so it updates on
  // every push instead of a hand-typed string. Backend stamps `settings.
  // last_deployment` on each container boot via the /api/analytics/deployment
  // hook, and returns it under `last_deployment` on the summary endpoint.
  const [autoVersion, setAutoVersion] = useState('');
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/analytics/summary`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data?.last_deployment) return;
        const d = new Date(data.last_deployment);
        if (Number.isNaN(d.getTime())) return;
        const parts = new Intl.DateTimeFormat('en-CA', {
          timeZone: 'America/Chicago',
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', hour12: false,
        }).formatToParts(d);
        const g = (t) => parts.find((p) => p.type === t)?.value || '';
        setAutoVersion(`${g('year')}.${g('month')}.${g('day')}.${g('hour')}${g('minute')}`);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Get footer settings with defaults
  const footerSettings = settings || {
    copyright_text: '\u00a9 {year} Shire Advisors, LLC. All rights reserved.',
    privacy_policy_url: '#',
    terms_url: '#',
  };
  
  // Replace {year} placeholder with actual year
  const copyrightText = footerSettings.copyright_text.replace('{year}', currentYear);

  // Build quick links from visible panels (built-in + custom)
  const quickLinks = visiblePanels
    .filter(panel => panel.visible)
    .sort((a, b) => a.order - b.order)
    .map(panel => {
      if (panelNavConfig[panel.id]) {
        return panelNavConfig[panel.id];
      }
      if (panel.isCustom && panel.name) {
        return { name: panel.name, href: `#${panel.id}` };
      }
      return null;
    })
    .filter(Boolean);

  const scrollToSection = (href) => {
    // If we're not on the home page, navigate there first, then scroll
    if (location.pathname !== '/') {
      navigate('/' + href);
      return;
    }
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gray-100 text-navy-900">
      {/* CTA Section */}
      <div className="bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="text-2xl sm:text-3xl font-serif font-bold mb-2 text-white">
                {ctaHeader.title}
              </h3>
              <p className="text-white/70">
                {ctaHeader.description}
              </p>
            </div>
            <button
              onClick={() => scrollToSection('#contact')}
              className="flex items-center gap-2 px-8 py-4 bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold rounded-lg transition-all duration-300 hover:shadow-xl hover:shadow-gold-500/20"
            >
              Get Started
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-12 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-3">
            <div className="mb-6">
              <img 
                src={LOGO_WHITE_BG} 
                alt="Shire Advisors" 
                className="h-12 w-auto"
                style={{ mixBlendMode: 'multiply' }}
              />
            </div>
            <p className="text-gray-600 leading-relaxed mb-6">
              Your trusted partner in business advisory, M&A, financial consulting, and strategic investments.
            </p>
            {socialMedia && socialMedia.length > 0 && (
              <div className="flex gap-4">
                {socialMedia.sort((a, b) => a.order - b.order).map((social) => {
                  const Icon = social.id === 'linkedin' ? Linkedin 
                    : social.id === 'twitter' ? Twitter 
                    : social.id === 'facebook' ? Facebook 
                    : social.id === 'instagram' ? Instagram 
                    : social.id === 'youtube' ? Youtube 
                    : Mail;
                  return (
                    <a 
                      key={social.id}
                      href={social.url} 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-navy-900 text-white rounded-lg flex items-center justify-center hover:bg-gold-500 hover:text-navy-900 transition-colors"
                    >
                      <Icon className="w-5 h-5" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <h4 className="text-lg font-semibold mb-6 text-navy-900">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(link.href);
                    }}
                    className="text-gray-600 hover:text-gold-600 transition-colors whitespace-nowrap"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="/urlsforsale"
                  className="text-gray-600 hover:text-gold-600 transition-colors whitespace-nowrap"
                  data-testid="footer-urls-for-sale-link"
                >
                  URLs for Sale
                </a>
              </li>
              <li>
                <a
                  href="/realestateforsale"
                  className="text-gray-600 hover:text-gold-600 transition-colors whitespace-nowrap"
                  data-testid="footer-real-estate-for-sale-link"
                >
                  Property for Sale
                </a>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div className="lg:col-span-3">
            <h4 className="text-lg font-semibold mb-6 text-navy-900">Our Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.id || service.title}>
                  <a
                    href="#services"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('#services');
                    }}
                    className="text-gray-600 hover:text-gold-600 transition-colors whitespace-nowrap"
                  >
                    {service.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-4">
            <h4 className="text-lg font-semibold mb-6 text-navy-900">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gold-600 mt-0.5 flex-shrink-0" />
                <div className="text-gray-600 space-y-1">
                  {(contactInfo.address || '')
                    .split(/\r?\n| - /)
                    .map((line) => line.trim())
                    .filter(Boolean)
                    .map((line, i) => (
                      <div key={i} className="whitespace-nowrap">{line}</div>
                    ))}
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-gold-600 flex-shrink-0" />
                <div className="text-gray-600">
                  {contactInfo.phone && <div>{contactInfo.phone}</div>}
                  {contactInfo.phone_alt && <div>{contactInfo.phone_alt}</div>}
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-gold-600 flex-shrink-0" />
                <a href={`mailto:${contactInfo.email}`} className="text-gray-600 hover:text-gold-600 transition-colors">
                  {contactInfo.email}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <p className="text-gray-500 text-sm">
                {copyrightText}
              </p>
              {autoVersion && (
                <span
                  className="text-gray-400 text-xs"
                  title={`Last deployed ${autoVersion} (Central Time)`}
                  data-testid="footer-auto-version"
                >
                  v{autoVersion}
                </span>
              )}
            </div>
            <div className="flex items-center gap-6">
              <button 
                onClick={() => setShowPrivacyModal(true)}
                className="text-gray-500 text-sm hover:text-navy-900 transition-colors"
              >
                Privacy Policy
              </button>
              <button 
                onClick={() => setShowTermsModal(true)}
                className="text-gray-500 text-sm hover:text-navy-900 transition-colors"
              >
                Terms of Service
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Privacy Policy Modal */}
      <LegalModal 
        isOpen={showPrivacyModal} 
        onClose={() => setShowPrivacyModal(false)}
        title="Privacy Policy"
      >
        <h3 className="font-semibold text-navy-900 mb-2">Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</h3>
        
        <p className="mb-4">Shire Advisors, LLC ("we," "our," or "us") respects your privacy and is committed to protecting the personal information you share with us. This Privacy Policy explains how we collect, use, disclose, and safeguard your information.</p>
        
        <h4 className="font-semibold text-navy-900 mt-4 mb-2">Information We Collect</h4>
        <p className="mb-2">We may collect personal information that you voluntarily provide to us when you:</p>
        <ul className="list-disc pl-5 mb-4 space-y-1">
          <li>Fill out contact forms on our website</li>
          <li>Request a consultation or our services</li>
          <li>Subscribe to our newsletter or communications</li>
          <li>Communicate with us via email, phone, or other means</li>
        </ul>
        <p className="mb-4">This information may include your name, email address, phone number, company name, and any other information you choose to provide.</p>
        
        <h4 className="font-semibold text-navy-900 mt-4 mb-2">How We Use Your Information</h4>
        <p className="mb-2">We use the information we collect to:</p>
        <ul className="list-disc pl-5 mb-4 space-y-1">
          <li>Respond to your inquiries and provide requested services</li>
          <li>Send you relevant business communications</li>
          <li>Improve our website and services</li>
          <li>Comply with legal obligations</li>
        </ul>
        
        <h4 className="font-semibold text-navy-900 mt-4 mb-2">Information Sharing</h4>
        <p className="mb-4">We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except as required by law or to trusted service providers who assist us in operating our business.</p>
        
        <h4 className="font-semibold text-navy-900 mt-4 mb-2">Data Security</h4>
        <p className="mb-4">We implement appropriate security measures to protect your personal information. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.</p>
        
        <h4 className="font-semibold text-navy-900 mt-4 mb-2">Your Rights</h4>
        <p className="mb-4">You may request access to, correction of, or deletion of your personal information by contacting us at {contactInfo.email}.</p>
        
        <h4 className="font-semibold text-navy-900 mt-4 mb-2">Contact Us</h4>
        <p>If you have questions about this Privacy Policy, please contact us at:<br/>{contactInfo.email}<br/>{contactInfo.phone}</p>
      </LegalModal>

      {/* Terms of Service Modal */}
      <LegalModal 
        isOpen={showTermsModal} 
        onClose={() => setShowTermsModal(false)}
        title="Terms of Service"
      >
        <h3 className="font-semibold text-navy-900 mb-2">Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</h3>
        
        <p className="mb-4">Please read these Terms of Service ("Terms") carefully before using the Shire Advisors, LLC website or services. By accessing or using our services, you agree to be bound by these Terms.</p>
        
        <h4 className="font-semibold text-navy-900 mt-4 mb-2">Services</h4>
        <p className="mb-4">Shire Advisors, LLC provides strategic consulting services, financial analysis, and related business advisory services. The specific terms of any engagement will be outlined in a separate service agreement.</p>
        
        <h4 className="font-semibold text-navy-900 mt-4 mb-2">Use of Website</h4>
        <p className="mb-2">You agree to use our website only for lawful purposes and in accordance with these Terms. You agree not to:</p>
        <ul className="list-disc pl-5 mb-4 space-y-1">
          <li>Use the website in any way that violates applicable laws or regulations</li>
          <li>Attempt to gain unauthorized access to any part of the website</li>
          <li>Interfere with the proper working of the website</li>
          <li>Use any automated means to access the website without our permission</li>
        </ul>
        
        <h4 className="font-semibold text-navy-900 mt-4 mb-2">Intellectual Property</h4>
        <p className="mb-4">All content on this website, including text, graphics, logos, and images, is the property of Shire Advisors, LLC and is protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without our written permission.</p>
        
        <h4 className="font-semibold text-navy-900 mt-4 mb-2">Confidentiality</h4>
        <p className="mb-4">We treat all client information as strictly confidential. Any information shared with us during consultations or through our services will be handled with the utmost discretion and will not be disclosed to third parties without your consent, except as required by law.</p>
        
        <h4 className="font-semibold text-navy-900 mt-4 mb-2">Disclaimer of Warranties</h4>
        <p className="mb-4">Our website and services are provided "as is" without warranties of any kind. We do not guarantee that our services will meet your specific requirements or that results will be achieved.</p>
        
        <h4 className="font-semibold text-navy-900 mt-4 mb-2">Limitation of Liability</h4>
        <p className="mb-4">To the fullest extent permitted by law, Shire Advisors, LLC shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our website or services.</p>
        
        <h4 className="font-semibold text-navy-900 mt-4 mb-2">Governing Law</h4>
        <p className="mb-4">These Terms shall be governed by and construed in accordance with the laws of the State of Texas, without regard to its conflict of law provisions.</p>
        
        <h4 className="font-semibold text-navy-900 mt-4 mb-2">Changes to Terms</h4>
        <p className="mb-4">We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to the website.</p>
        
        <h4 className="font-semibold text-navy-900 mt-4 mb-2">Contact Us</h4>
        <p>If you have questions about these Terms, please contact us at:<br/>{contactInfo.email}<br/>{contactInfo.phone}</p>
      </LegalModal>
    </footer>
  );
};

export default Footer;
